import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeading } from '@/components/page-heading'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SelectShowNotice } from '@/components/current-show'
import { useCurrentShow } from '@/context/current-show'
import { useRunMode } from '@/context/run-mode'
import {
  useDeleteEntry,
  useDeleteShowExhibitor,
  useExhibitors,
  useShowClubs,
  useUpdateShowExhibitor,
} from '@/api/hooks'
import type { TExhibitorWithEntries, TShow } from '@schemas'
import { useActorPermissions, formatRoleList } from '@/hooks/useActorPermissions'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import {
  buildShowEntrantClubUpdatePayload,
  normalizeShowEntrantClubSelection,
  updateExhibitorCacheAfterShowEntrant,
} from '@/features/entrants/showEntrantClub'

type RouteParams = {
  exhibitorId?: string
}

type EntrySummary = NonNullable<TExhibitorWithEntries['entries']>[number] & {
  updatedAt?: string | null
  createdAt?: string | null
}

function parseTimestamp(value?: string | null): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

function formatContact(email?: string | null, phone?: string | null): string {
  const parts = [email?.trim(), phone?.trim()].filter(Boolean)
  return parts.length > 0 ? parts.join(' • ') : 'No contact info'
}

function formatStatus(status?: string | null): string {
  switch (status) {
    case 'checked_in':
      return 'Checked in'
    case 'not_entered':
      return 'Not Entered'
    case 'judged':
      return 'Judged'
    case 'finalized':
      return 'Finalized'
    case 'disqualified':
      return 'Disqualified'
    case 'draft':
    default:
      return 'Draft'
  }
}

export default function EntrantPage() {
  const { exhibitorId } = useParams<RouteParams>()
  const { currentShow } = useCurrentShow()
  const { mode } = useRunMode()
  const showId = currentShow?.id
  const exhibitorsQuery = useExhibitors(
    { showId, limit: 500 },
    { enabled: mode === 'run' && Boolean(showId) }
  )

  if (mode !== 'run' || !currentShow) {
    return (
      <SelectShowNotice
        title="Select a show to enter Run mode"
        description="Choose a show and switch to Run mode to view entrant details."
        actionLabel="Choose show"
      />
    )
  }

  if (!exhibitorId) {
    return (
      <div className="space-y-6">
        <header className="space-y-2">
          <PageHeading runLevel={2}>Entrant</PageHeading>
        </header>
        <div className="rounded-md border border-dashed bg-muted/20 p-6 text-sm text-muted-foreground">
          Entrant not found for this show.
        </div>
      </div>
    )
  }

  if (exhibitorsQuery.isLoading) {
    return (
      <div className="space-y-6">
        <header className="space-y-2">
          <PageHeading runLevel={2}>Entrant</PageHeading>
        </header>
        <Card>
          <CardHeader>
            <CardTitle>Loading entrant…</CardTitle>
            <CardDescription>Fetching entrant details for the selected show.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please hold while we load the entrant and their entries.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (exhibitorsQuery.isError) {
    return (
      <div className="space-y-6">
        <header className="space-y-2">
          <PageHeading runLevel={2}>Entrant</PageHeading>
        </header>
        <Card role="alert" className="border-destructive/50 bg-destructive/10">
          <CardHeader>
            <CardTitle>Unable to load entrant</CardTitle>
            <CardDescription>
              {exhibitorsQuery.error?.message ?? 'Unexpected error'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const exhibitor = exhibitorsQuery.data?.find(candidate => candidate.id === exhibitorId) ?? null

  if (!exhibitor) {
    return (
      <div className="space-y-6">
        <header className="space-y-2">
          <PageHeading runLevel={2}>Entrant</PageHeading>
        </header>
        <div className="rounded-md border border-dashed bg-muted/20 p-6 text-sm text-muted-foreground">
          Entrant not found for this show.
        </div>
      </div>
    )
  }

  return (
    <EntrantDetail
      key={`${currentShow.id}:${exhibitor.id}`}
      currentShow={currentShow}
      exhibitor={exhibitor}
    />
  )
}

function EntrantDetail({
  currentShow,
  exhibitor,
}: {
  currentShow: TShow
  exhibitor: TExhibitorWithEntries
}) {
  const navigate = useNavigate()
  const currentSlug = useShowSlug()
  const queryClient = useQueryClient()
  const showId = currentShow.id
  const displayName = exhibitor.name ?? exhibitor.person?.name ?? 'Unnamed entrant'
  const contactEmail = exhibitor.person?.email ?? exhibitor.email ?? null
  const contactPhone = exhibitor.person?.phone ?? exhibitor.phone ?? null
  const linkedPerson =
    exhibitor.person?.name && exhibitor.person.name !== exhibitor.name
      ? exhibitor.person.name
      : null

  const entries = useMemo(() => {
    const list = (exhibitor.entries ?? []) as EntrySummary[]
    return [...list].sort((a, b) => {
      const aTimestamp = parseTimestamp(a.updatedAt ?? a.createdAt)
      const bTimestamp = parseTimestamp(b.updatedAt ?? b.createdAt)
      if (aTimestamp && bTimestamp) {
        return bTimestamp - aTimestamp
      }
      if (bTimestamp && !aTimestamp) {
        return 1
      }
      if (aTimestamp && !bTimestamp) {
        return -1
      }
      const codeA = a.entryCode ?? ''
      const codeB = b.entryCode ?? ''
      return codeA.localeCompare(codeB)
    })
  }, [exhibitor.entries])

  const permissions = useActorPermissions(showId)
  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const clubsEnabled = currentShow?.enableClubs !== false
  const updateRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('exhibitors:write')),
    [permissions]
  )
  const deleteRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entries:write')),
    [permissions]
  )
  const canUpdateClub = permissions.can('exhibitors:write')
  const canDeleteEntries = permissions.can('entries:write')

  const updateShowExhibitor = useUpdateShowExhibitor()
  const deleteEntry = useDeleteEntry()
  const deleteShowExhibitor = useDeleteShowExhibitor()

  const [clubDialogOpen, setClubDialogOpen] = useState(false)
  const [selectedClubId, setSelectedClubId] = useState<string>('none')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletePending, setDeletePending] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)

  const showClubsQuery = useShowClubs(
    showId,
    { limit: 100 },
    { enabled: clubsEnabled && Boolean(showId) }
  )
  const showClubs = clubsEnabled ? (showClubsQuery.data ?? []) : []
  const showClubOverrideName =
    exhibitor.showClubName ??
    (exhibitor.showClubId
      ? (showClubs.find(club => club.id === exhibitor.showClubId)?.name ?? null)
      : null)
  const initialSelectedClubId = exhibitor.showClubId ?? 'none'
  useEffect(() => {
    setClubDialogOpen(false)
  }, [clubsEnabled, showId])

  useEffect(() => {
    if (!clubDialogOpen || !clubsEnabled) return
    setSelectedClubId(initialSelectedClubId)
  }, [clubDialogOpen, clubsEnabled, initialSelectedClubId])

  const isClubDirty = selectedClubId !== initialSelectedClubId
  const saveClubDisabled = updateShowExhibitor.isPending || !isClubDirty
  const showNameLabel = currentShow.name ?? 'this show'

  const deletableEntries = useMemo(
    () =>
      entries.filter(
        entry =>
          entry.status === 'not_entered' && typeof entry.id === 'string' && entry.id.length > 0
      ),
    [entries]
  )

  const updateClubDisabledReason = !showId
    ? 'Select a show before updating clubs.'
    : permissionLoading
      ? 'Loading permissions…'
      : !canUpdateClub
        ? `Updating clubs requires ${updateRolesLabel}.`
        : undefined
  const updateClubDisabled = Boolean(updateClubDisabledReason) || updateShowExhibitor.isPending
  const updateClubTooltip = updateShowExhibitor.isPending
    ? 'Updating club…'
    : updateClubDisabledReason

  const showAllowsDeleteEntries = currentShow?.status !== 'finalized'

  const deleteEntriesDisabledReason = !showAllowsDeleteEntries
    ? 'Deleting unentered entries is unavailable once the show is finalized.'
    : !showId
      ? 'Select a show before deleting entries.'
      : permissionLoading
        ? 'Loading permissions…'
        : !canDeleteEntries
          ? `Deleting entries requires ${deleteRolesLabel}.`
          : deletableEntries.length === 0
            ? 'This entrant has no unentered entries.'
            : undefined
  const deleteEntriesDisabled = Boolean(deleteEntriesDisabledReason) || deletePending
  const deleteEntriesTooltip = deletePending ? 'Deleting entries…' : deleteEntriesDisabledReason

  const hasNoEntries = entries.length === 0

  const removeEntrantDisabledReason = !showId
    ? 'Select a show before removing entrants.'
    : permissionLoading
      ? 'Loading permissions…'
      : !canUpdateClub
        ? `Removing entrants requires ${updateRolesLabel}.`
        : !hasNoEntries
          ? 'Cannot remove while entries exist for this show.'
          : undefined
  const removeEntrantDisabled =
    Boolean(removeEntrantDisabledReason) || deleteShowExhibitor.isPending
  const removeEntrantTooltip = deleteShowExhibitor.isPending
    ? 'Removing entrant…'
    : removeEntrantDisabledReason

  const handleClubDialogChange = useCallback(
    (open: boolean) => {
      if (open && (!clubsEnabled || updateClubDisabledReason)) return
      setClubDialogOpen(open)
    },
    [clubsEnabled, updateClubDisabledReason]
  )

  const handleDeleteDialogChange = useCallback(
    (open: boolean) => {
      if (open && deleteEntriesDisabledReason) return
      setDeleteDialogOpen(open)
    },
    [deleteEntriesDisabledReason]
  )

  const handleRemoveDialogChange = useCallback(
    (open: boolean) => {
      if (open && removeEntrantDisabledReason) return
      setRemoveDialogOpen(open)
    },
    [removeEntrantDisabledReason]
  )

  const handleSubmitClubUpdate = useCallback(async () => {
    if (!clubsEnabled || !showId || !exhibitor.id || updateClubDisabled) return

    const selection = normalizeShowEntrantClubSelection({
      clubId: selectedClubId === 'none' ? null : selectedClubId,
      clubName: null,
    })
    const currentSelection = normalizeShowEntrantClubSelection({
      clubId: exhibitor.showClubId ?? null,
      clubName: exhibitor.showClubName ?? null,
    })

    const payload = buildShowEntrantClubUpdatePayload({
      next: selection,
      current: currentSelection,
    })

    if (!payload) {
      toast.info('No club changes to save.')
      setClubDialogOpen(false)
      return
    }

    try {
      const showEntrant = await updateShowExhibitor.mutateAsync({
        showId,
        exhibitorId: exhibitor.id,
        payload,
      })
      updateExhibitorCacheAfterShowEntrant({
        queryClient,
        showId,
        exhibitorId: exhibitor.id,
        showEntrant,
      })
      toast.success('Club updated')
      setSelectedClubId(showEntrant.clubId ?? 'none')
      setClubDialogOpen(false)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to update club. Try again shortly.'
      toast.error(message)
    }
  }, [
    clubsEnabled,
    exhibitor.id,
    exhibitor.showClubId,
    exhibitor.showClubName,
    queryClient,
    selectedClubId,
    showId,
    updateShowExhibitor,
    updateClubDisabled,
  ])

  const handleDeleteUnentered = useCallback(async () => {
    if (deleteEntriesDisabled || deletableEntries.length === 0) return
    setDeletePending(true)
    try {
      for (const entry of deletableEntries) {
        if (!entry.id) continue
        await deleteEntry.mutateAsync(entry.id)
      }
      const count = deletableEntries.length
      toast.success(
        `Removed ${count} unentered entr${count === 1 ? 'y' : 'ies'} from this entrant.`
      )
      setDeleteDialogOpen(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete unentered entries.'
      toast.error(message)
    } finally {
      setDeletePending(false)
    }
  }, [deleteEntriesDisabled, deletableEntries, deleteEntry])

  const handleRemoveEntrant = useCallback(async () => {
    if (!showId || !exhibitor.id) return
    try {
      await deleteShowExhibitor.mutateAsync({ showId, exhibitorId: exhibitor.id })
      toast.success('Entrant removed from show')
      setRemoveDialogOpen(false)
      navigate(showAware('/entrants', { currentSlug }))
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to remove entrant from this show. Try again shortly.'

      if (error instanceof Error && /\[404]/.test(error.message)) {
        toast.info('Entrant not found for this show. It may have already been removed.')
        setRemoveDialogOpen(false)
        navigate(showAware('/entrants', { currentSlug }))
        return
      }

      toast.error(message)
    }
  }, [currentSlug, deleteShowExhibitor, exhibitor.id, navigate, showId])

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <PageHeading runLevel={2}>{`Entrant > ${displayName}`}</PageHeading>
        <p className="text-muted-foreground">
          Quick reference view of entrant details and recent entries for show staff.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{displayName}</CardTitle>
          <CardDescription>
            {currentShow.name ? `Active in ${currentShow.name}` : 'Active entrant'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Contact
              </dt>
              <dd className="text-sm text-foreground">
                {formatContact(contactEmail, contactPhone)}
              </dd>
            </div>
            {clubsEnabled ? (
              <div className="space-y-1">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Show club
                </dt>
                <dd className="text-sm text-foreground">{showClubOverrideName ?? '—'}</dd>
              </div>
            ) : null}
          </dl>
          {linkedPerson ? (
            <div className="rounded-md border border-muted/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Linked person: <span className="font-medium text-foreground">{linkedPerson}</span>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-2">
            {clubsEnabled ? (
              <Dialog open={clubDialogOpen} onOpenChange={handleClubDialogChange}>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={updateClubDisabled}
                    title={updateClubTooltip}
                  >
                    {updateShowExhibitor.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                        Updating…
                      </>
                    ) : (
                      'Update club'
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent contentClassName="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Update club</DialogTitle>
                    <DialogDescription>
                      Set the entrant&apos;s club for {currentShow.name}. Changes refresh the
                      Entrants list automatically.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor="entrant-club-select">Club</Label>
                    <Select
                      value={selectedClubId}
                      onValueChange={setSelectedClubId}
                      disabled={showClubsQuery.isLoading}
                    >
                      <SelectTrigger
                        id="entrant-club-select"
                        aria-label="Select club"
                        data-testid="club-select-trigger"
                      >
                        <SelectValue placeholder="Select a club" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No club</SelectItem>
                        {showClubs.map(club =>
                          club.id ? (
                            <SelectItem key={club.id} value={club.id}>
                              {club.name}
                            </SelectItem>
                          ) : null
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Updates apply only to {showNameLabel}. Select &quot;No club&quot; to clear the
                      show-level club.
                    </p>
                  </div>
                  <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setClubDialogOpen(false)}
                      disabled={updateShowExhibitor.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSubmitClubUpdate}
                      disabled={saveClubDisabled}
                    >
                      {updateShowExhibitor.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                          Saving…
                        </>
                      ) : (
                        'Save changes'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : null}
            {showAllowsDeleteEntries ? (
              <Dialog open={deleteDialogOpen} onOpenChange={handleDeleteDialogChange}>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={deleteEntriesDisabled}
                    title={deleteEntriesTooltip}
                  >
                    {deletePending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                        Deleting…
                      </>
                    ) : (
                      'Delete unentered entries'
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent contentClassName="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Delete unentered entries</DialogTitle>
                    <DialogDescription>
                      Permanently remove {deletableEntries.length} entry
                      {deletableEntries.length === 1 ? '' : 'ies'} marked as Not Entered.
                    </DialogDescription>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    This action cannot be undone. Deleted Exhibit IDs remain retired for this show.
                  </p>
                  <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDeleteDialogOpen(false)}
                      disabled={deletePending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDeleteUnentered}
                      disabled={deletePending}
                    >
                      {deletePending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                          Removing…
                        </>
                      ) : (
                        'Delete entries'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : null}
            {hasNoEntries ? (
              <Dialog open={removeDialogOpen} onOpenChange={handleRemoveDialogChange}>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={removeEntrantDisabled}
                    title={removeEntrantTooltip}
                  >
                    {deleteShowExhibitor.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                        Removing…
                      </>
                    ) : (
                      'Remove from show'
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent contentClassName="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Remove entrant from show</DialogTitle>
                    <DialogDescription>
                      Remove {displayName} from {currentShow.name}. Their exhibitor profile and
                      history remain intact.
                    </DialogDescription>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    This action is only available when the entrant has no entries for this show and
                    cannot be undone.
                  </p>
                  <DialogFooter className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="default"
                      onClick={() => setRemoveDialogOpen(false)}
                      disabled={deleteShowExhibitor.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleRemoveEntrant}
                      disabled={deleteShowExhibitor.isPending}
                    >
                      {deleteShowExhibitor.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                          Removing…
                        </>
                      ) : (
                        'Remove'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Entries</CardTitle>
          <CardDescription>Recent exhibits linked to this entrant.</CardDescription>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No entries recorded for this entrant in the selected show.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Exhibit ID</TableHead>
                  <TableHead className="w-[220px]">Class</TableHead>
                  <TableHead className="w-[120px] text-right">Status</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(entry => {
                  const classLabel = entry.class
                    ? `${entry.class.code} • ${entry.class.name}`
                    : 'Class pending'
                  const statusLabel = formatStatus(entry.status)
                  const entryDetailPath =
                    typeof entry.id === 'string' && entry.id.length > 0
                      ? showAware(`/entries/${entry.id}`, { currentSlug })
                      : null
                  const viewButtonLabel =
                    entry.entryCode && entry.entryCode.length > 0
                      ? `View ${entry.entryCode}`
                      : 'View entry detail'
                  return (
                    <TableRow key={entry.id}>
                      <TableCell className="font-mono text-sm">{entry.entryCode ?? '—'}</TableCell>
                      <TableCell>{classLabel}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={entry.status === 'checked_in' ? 'secondary' : 'outline'}>
                          {statusLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (entryDetailPath) {
                              navigate(entryDetailPath)
                            }
                          }}
                          disabled={!entryDetailPath}
                          aria-label={viewButtonLabel}
                          title={entryDetailPath ? viewButtonLabel : 'Entry detail unavailable'}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
